import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { formatHalaman, halamanToInput } from '@/utils/halaman';
import { ArrowLeft, Search, History, X, Pencil, Save, Trash2, ChevronRight, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TABS = [
  { key: 'ziyadah',   label: 'Ziyadah'   },
  { key: 'murojaah',  label: 'Murojaah'  },
  { key: 'binnadzar', label: 'Binnadzar' },
  { key: 'tasmi',     label: "Tasmi'"    },
  { key: 'ujian',     label: 'Ujian'     },
];

// Ziyadah options (same pattern as InputSetoran)
const ZIYADAH_OPTS = (() => {
  const opts = [{ value: 0.25, label: '1/4' }, { value: 1/3, label: '1/3' }, { value: 0.5, label: '1/2' }];
  for (let n = 1; n <= 10; n++) {
    opts.push({ value: n,      label: halamanToInput(n)      });
    opts.push({ value: n+0.25, label: halamanToInput(n+0.25) });
    opts.push({ value: n+1/3,  label: halamanToInput(n+1/3)  });
    opts.push({ value: n+0.5,  label: halamanToInput(n+0.5)  });
  }
  return opts;
})();

const NILAI_OPTS = ['A', 'A-', 'B', 'B+', 'B-', 'C', 'Lulus', 'Remedial'];

function fmtDate(str) {
  if (!str) return '-';
  const d = new Date(str);
  if (isNaN(d)) return str;
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
}

/* ─────────── EDIT MODAL ─────────── */
function EditModal({ rec, tabKey, onClose, onSaved, onDeleted }) {
  const [data, setData] = useState(() => {
    if (tabKey === 'ujian') {
      return {
        tajwid_nilai: rec.tajwid_nilai || 'A',
        mhq: rec.mhq ? [...rec.mhq] : [],
        murojaah: rec.murojaah ? [...rec.murojaah] : [],
        catatan: rec.catatan || '',
      };
    }
    if (tabKey === 'tasmi') {
      return {
        tanggal: rec.tanggal || '',
        tipe: rec.tipe || '1juz',
        juz: rec.juz || 1,
        juz_dari: rec.juz_dari || 1,
        juz_sampai: rec.juz_sampai || 1,
        jumlah_juz: rec.jumlah_juz || 1,
        penilaian: rec.penilaian || rec.keterangan || 'Lulus',
      };
    }
    return {
      tanggal: rec.tanggal || '',
      juz: rec.juz || 1,
      dari_halaman: rec.dari_halaman || 1,
      sampai_halaman: rec.sampai_halaman || 1,
      jml_halaman: rec.jml_halaman || 0.5,
      catatan: rec.catatan || '',
    };
  });
  const [step, setStep] = useState('edit'); // 'edit' | 'confirm' | 'confirmDelete'
  const [saving, setSaving] = useState(false);

  const set = (key) => (e) => setData(d => ({ ...d, [key]: e.target.value }));

  // Build summary rows for confirmation screen
  const summaryRows = () => {
    if (tabKey === 'ujian') return [
      { label: 'Tajwid', value: data.tajwid_nilai },
      { label: 'MHQ', value: data.mhq.length > 0 ? data.mhq.map(m => `Juz ${m.juz} (${m.nilai})`).join(', ') : '-' },
      { label: 'Murojaah', value: data.murojaah.length > 0 ? data.murojaah.map(m => `Juz ${m.juz} (${m.nilai})`).join(', ') : '-' },
      ...(data.catatan ? [{ label: 'Catatan', value: data.catatan }] : []),
    ];
    if (tabKey === 'tasmi') return [
      { label: 'Tanggal', value: data.tanggal },
      { label: 'Tipe', value: data.tipe === 'multi' ? `Multi: Juz ${data.juz_dari}–${data.juz_sampai}` : `Juz ${data.juz}` },
      { label: 'Hasil', value: data.penilaian },
    ];
    return [
      { label: 'Tanggal', value: data.tanggal },
      { label: 'Juz', value: String(data.juz) },
      { label: 'Jml Halaman', value: formatHalaman(data.jml_halaman) + ' hal' },
      { label: 'Hal', value: `${data.dari_halaman} – ${data.sampai_halaman}` },
      ...(data.catatan ? [{ label: 'Catatan', value: data.catatan }] : []),
    ];
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let url, body;
      if (tabKey === 'ujian') {
        url = `${API}/ujian-tahfidz/${rec.id}`;
        body = { santri_id: rec.santri_id, semester: rec.semester, tajwid_nilai: data.tajwid_nilai, mhq: data.mhq, murojaah: data.murojaah, catatan: data.catatan || undefined };
      } else if (tabKey === 'tasmi') {
        url = `${API}/tasmi/${rec.id}`;
        const isMulti = data.tipe === 'multi';
        body = { santri_id: rec.santri_id, halaqah_id: rec.halaqah_id, tanggal: data.tanggal, tipe: data.tipe, juz: isMulti ? undefined : Number(data.juz), juz_dari: isMulti ? Number(data.juz_dari) : undefined, juz_sampai: isMulti ? Number(data.juz_sampai) : undefined, jumlah_juz: isMulti ? Number(data.jumlah_juz) : 1, penilaian: data.penilaian, keterangan: data.penilaian };
      } else {
        url = `${API}/setoran/${rec.id}`;
        body = { ...rec, id: undefined, tanggal: data.tanggal, juz: Number(data.juz), dari_halaman: Number(data.dari_halaman), sampai_halaman: Number(data.sampai_halaman), jml_halaman: Number(data.jml_halaman), catatan: data.catatan || undefined };
      }
      const res = await axios.put(url, body, { withCredentials: true });
      toast.success('Perubahan disimpan');
      onSaved(res.data);
    } catch { toast.error('Gagal menyimpan perubahan'); setStep('edit'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const urlMap = { ujian: `${API}/ujian-tahfidz/${rec.id}`, tasmi: `${API}/tasmi/${rec.id}` };
      const url = urlMap[tabKey] || `${API}/setoran/${rec.id}`;
      await axios.delete(url, { withCredentials: true });
      toast.success('Data berhasil dihapus');
      onDeleted(rec.id);
    } catch { toast.error('Gagal menghapus data'); setStep('edit'); }
    finally { setSaving(false); }
  };

  const tabLabel = tabKey === 'ujian' ? 'Ujian Tahfidz' : tabKey === 'tasmi' ? "Tasmi'" : TABS.find(t => t.key === tabKey)?.label;
  const inputCls = "w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
  const labelCls = "block text-[10px] font-semibold text-gray-500 uppercase mb-1";

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={step === 'edit' ? onClose : undefined} />
      <div className="relative bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-sm max-h-[92vh] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            {step === 'confirmDelete' ? <AlertTriangle size={16} className="text-red-500" /> : <Pencil size={16} className="text-emerald-600" />}
            <h3 className="text-sm font-bold text-gray-800">
              {step === 'confirm' ? 'Konfirmasi Perubahan' : step === 'confirmDelete' ? 'Hapus Data?' : `Edit ${tabLabel}`}
            </h3>
          </div>
          <button onClick={onClose} data-testid="edit-modal-close"
            className="p-1.5 hover:bg-gray-100 rounded-xl transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* ── STEP: EDIT FORM ── */}
        {step === 'edit' && (
          <div className="overflow-y-auto px-5 py-4 space-y-3 flex-1">
            {(tabKey === 'ziyadah' || tabKey === 'murojaah' || tabKey === 'binnadzar') && (
              <>
                <div>
                  <label className={labelCls}>Tanggal</label>
                  <input type="date" value={data.tanggal} onChange={set('tanggal')} className={inputCls} data-testid="edit-tanggal" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Juz</label>
                    <input type="number" min={1} max={30} value={data.juz}
                      onChange={e => setData(d => ({ ...d, juz: e.target.value }))}
                      className={inputCls} data-testid="edit-juz" />
                  </div>
                  <div>
                    <label className={labelCls}>Jml Halaman</label>
                    {tabKey === 'ziyadah' ? (
                      <select value={data.jml_halaman}
                        onChange={e => setData(d => ({ ...d, jml_halaman: Number(e.target.value) }))}
                        className={inputCls} data-testid="edit-jml-halaman">
                        {ZIYADAH_OPTS.map(o => (
                          <option key={o.value} value={o.value}>{o.label} hal</option>
                        ))}
                      </select>
                    ) : (
                      <input type="number" min={0} step={0.5} value={data.jml_halaman}
                        onChange={e => setData(d => ({ ...d, jml_halaman: e.target.value }))}
                        className={inputCls} data-testid="edit-jml-halaman" />
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Dari Hal</label>
                    <input type="number" min={1} max={604} value={data.dari_halaman}
                      onChange={e => setData(d => ({ ...d, dari_halaman: e.target.value }))}
                      className={inputCls} data-testid="edit-dari-halaman" />
                  </div>
                  <div>
                    <label className={labelCls}>Sampai Hal</label>
                    <input type="number" min={1} max={604} value={data.sampai_halaman}
                      onChange={e => setData(d => ({ ...d, sampai_halaman: e.target.value }))}
                      className={inputCls} data-testid="edit-sampai-halaman" />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Catatan</label>
                  <textarea value={data.catatan} onChange={set('catatan')} rows={2}
                    className={inputCls + ' resize-none'} data-testid="edit-catatan" />
                </div>
              </>
            )}
            {tabKey === 'tasmi' && (
              <>
                <div>
                  <label className={labelCls}>Tanggal</label>
                  <input type="date" value={data.tanggal} onChange={set('tanggal')} className={inputCls} data-testid="edit-tanggal" />
                </div>
                <div>
                  <label className={labelCls}>Tipe</label>
                  <select value={data.tipe} onChange={set('tipe')} className={inputCls} data-testid="edit-tipe">
                    <option value="1juz">1 Juz</option>
                    <option value="multi">Multi Juz</option>
                  </select>
                </div>
                {data.tipe === '1juz' ? (
                  <div>
                    <label className={labelCls}>Juz</label>
                    <input type="number" min={1} max={30} value={data.juz}
                      onChange={e => setData(d => ({ ...d, juz: e.target.value }))}
                      className={inputCls} data-testid="edit-juz" />
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className={labelCls}>Dari Juz</label>
                      <input type="number" min={1} max={30} value={data.juz_dari}
                        onChange={e => setData(d => ({ ...d, juz_dari: e.target.value }))}
                        className={inputCls} data-testid="edit-juz-dari" />
                    </div>
                    <div>
                      <label className={labelCls}>Sampai</label>
                      <input type="number" min={1} max={30} value={data.juz_sampai}
                        onChange={e => setData(d => ({ ...d, juz_sampai: e.target.value }))}
                        className={inputCls} data-testid="edit-juz-sampai" />
                    </div>
                    <div>
                      <label className={labelCls}>Jml Juz</label>
                      <input type="number" min={1} max={30} value={data.jumlah_juz}
                        onChange={e => setData(d => ({ ...d, jumlah_juz: e.target.value }))}
                        className={inputCls} data-testid="edit-jumlah-juz" />
                    </div>
                  </div>
                )}
                <div>
                  <label className={labelCls}>Hasil</label>
                  <div className="flex flex-wrap gap-2">
                    {['Lulus', 'Remedial', 'A', 'A-', 'B', 'B-'].map(v => (
                      <button key={v} type="button" data-testid={`edit-penilaian-${v}`}
                        onClick={() => setData(d => ({ ...d, penilaian: v }))}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold border-2 transition-all ${
                          data.penilaian === v
                            ? v === 'Lulus' || v === 'A' ? 'bg-emerald-500 text-white border-emerald-500'
                              : v === 'Remedial' ? 'bg-red-500 text-white border-red-500'
                              : 'bg-amber-400 text-white border-amber-400'
                            : 'bg-white text-gray-400 border-gray-200'
                        }`}>{v}</button>
                    ))}
                  </div>
                </div>
              </>
            )}
            {tabKey === 'ujian' && (
              <>
                <div>
                  <label className={labelCls}>Tajwid</label>
                  <div className="flex gap-2">
                    {['A', 'B', 'C', 'D'].map(v => (
                      <button key={v} type="button" data-testid={`edit-tajwid-${v}`}
                        onClick={() => setData(d => ({ ...d, tajwid_nilai: v }))}
                        className={`flex-1 py-2 rounded-xl text-xs font-bold border-2 transition-all ${
                          data.tajwid_nilai === v ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-gray-400 border-gray-200'
                        }`}>{v}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className={labelCls + ' mb-0'}>MHQ</label>
                    <button type="button" onClick={() => setData(d => ({ ...d, mhq: [...(d.mhq || []), { juz: 1, nilai: 'A' }] }))}
                      className="text-[10px] text-emerald-600 font-bold">+ Tambah</button>
                  </div>
                  {(data.mhq || []).map((m, i) => (
                    <div key={i} className="flex gap-2 mb-2 items-center">
                      <input type="number" min={1} max={30} value={m.juz}
                        onChange={e => setData(d => { const mhq = [...d.mhq]; mhq[i] = { ...mhq[i], juz: Number(e.target.value) }; return { ...d, mhq }; })}
                        className={inputCls + ' w-16'} placeholder="Juz" />
                      <select value={m.nilai}
                        onChange={e => setData(d => { const mhq = [...d.mhq]; mhq[i] = { ...mhq[i], nilai: e.target.value }; return { ...d, mhq }; })}
                        className={inputCls + ' flex-1'}>
                        {NILAI_OPTS.map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                      <button type="button" onClick={() => setData(d => ({ ...d, mhq: d.mhq.filter((_, j) => j !== i) }))}
                        className="p-1.5 hover:bg-red-50 rounded-lg"><X size={14} className="text-red-400" /></button>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className={labelCls + ' mb-0'}>Murojaah</label>
                    <button type="button" onClick={() => setData(d => ({ ...d, murojaah: [...(d.murojaah || []), { juz: 1, setoran: '20 hal', nilai: 'A' }] }))}
                      className="text-[10px] text-emerald-600 font-bold">+ Tambah</button>
                  </div>
                  {(data.murojaah || []).map((m, i) => (
                    <div key={i} className="flex gap-2 mb-2 items-center">
                      <input type="number" min={1} max={30} value={m.juz}
                        onChange={e => setData(d => { const mur = [...d.murojaah]; mur[i] = { ...mur[i], juz: Number(e.target.value) }; return { ...d, murojaah: mur }; })}
                        className={inputCls + ' w-14'} placeholder="Juz" />
                      <input type="text" value={m.setoran}
                        onChange={e => setData(d => { const mur = [...d.murojaah]; mur[i] = { ...mur[i], setoran: e.target.value }; return { ...d, murojaah: mur }; })}
                        className={inputCls + ' flex-1'} placeholder="Setoran" />
                      <select value={m.nilai}
                        onChange={e => setData(d => { const mur = [...d.murojaah]; mur[i] = { ...mur[i], nilai: e.target.value }; return { ...d, murojaah: mur }; })}
                        className={inputCls + ' w-16'}>
                        {NILAI_OPTS.map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                      <button type="button" onClick={() => setData(d => ({ ...d, murojaah: d.murojaah.filter((_, j) => j !== i) }))}
                        className="p-1.5 hover:bg-red-50 rounded-lg"><X size={14} className="text-red-400" /></button>
                    </div>
                  ))}
                </div>
                <div>
                  <label className={labelCls}>Catatan</label>
                  <textarea value={data.catatan} onChange={set('catatan')} rows={2}
                    className={inputCls + ' resize-none'} data-testid="edit-catatan" />
                </div>
              </>
            )}
          </div>
        )}

        {/* ── STEP: CONFIRM SAVE ── */}
        {step === 'confirm' && (
          <div className="overflow-y-auto px-5 py-4 flex-1">
            <p className="text-xs text-gray-500 mb-3">Periksa data sebelum disimpan:</p>
            <div className="bg-gray-50 rounded-2xl overflow-hidden divide-y divide-gray-100">
              {summaryRows().map(row => (
                <div key={row.label} className="flex justify-between px-4 py-2.5">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase">{row.label}</span>
                  <span className="text-xs font-semibold text-gray-800 text-right max-w-[60%]">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── STEP: CONFIRM DELETE ── */}
        {step === 'confirmDelete' && (
          <div className="px-5 py-6 flex-1 flex flex-col items-center justify-center gap-3 text-center">
            <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center">
              <Trash2 size={24} className="text-red-500" />
            </div>
            <p className="text-sm font-bold text-gray-800">Hapus data {tabLabel} ini?</p>
            <p className="text-xs text-gray-400">Tindakan ini tidak dapat dibatalkan dan data akan hilang permanen.</p>
          </div>
        )}

        {/* Footer */}
        <div className="px-5 pb-6 pt-3 border-t border-gray-100">
          {step === 'edit' && (
            <div className="flex gap-2">
              <button onClick={() => setStep('confirmDelete')} data-testid="delete-btn"
                className="flex items-center justify-center gap-1.5 px-4 py-3 border-2 border-red-200 text-red-500 rounded-2xl text-sm font-semibold hover:bg-red-50 transition-colors">
                <Trash2 size={15} /> Hapus
              </button>
              <button onClick={onClose}
                className="flex-1 py-3 border border-gray-200 rounded-2xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                Batal
              </button>
              <button onClick={() => setStep('confirm')} data-testid="edit-modal-review"
                className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-2xl transition-colors text-sm">
                Lanjut <ChevronRight size={15} />
              </button>
            </div>
          )}
          {step === 'confirm' && (
            <div className="flex gap-3">
              <button onClick={() => setStep('edit')}
                className="flex-1 py-3 border border-gray-200 rounded-2xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                ← Kembali
              </button>
              <button onClick={handleSave} disabled={saving} data-testid="edit-modal-save"
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold rounded-2xl transition-colors text-sm">
                <Save size={15} />
                {saving ? 'Menyimpan...' : 'Konfirmasi Simpan'}
              </button>
            </div>
          )}
          {step === 'confirmDelete' && (
            <div className="flex gap-3">
              <button onClick={() => setStep('edit')}
                className="flex-1 py-3 border border-gray-200 rounded-2xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                Batal
              </button>
              <button onClick={handleDelete} disabled={saving} data-testid="confirm-delete-btn"
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-semibold rounded-2xl transition-colors text-sm">
                <Trash2 size={15} />
                {saving ? 'Menghapus...' : 'Ya, Hapus'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────── CARDS ─────────── */
function SetoranCard({ rec, santriName, onEdit }) {
  return (
    <div data-testid={`riwayat-card-${rec.id}`}
      className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-gray-100">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <span className="text-emerald-700 font-bold text-xs">{santriName?.charAt(0) ?? '?'}</span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate">{santriName || '-'}</p>
            <p className="text-[10px] text-gray-400">{fmtDate(rec.tanggal)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="text-right">
            <p className="text-xs font-bold text-emerald-700">Juz {rec.juz || '-'}</p>
            {rec.jml_halaman != null && rec.jml_halaman > 0 && (
              <p className="text-[10px] text-gray-400">{formatHalaman(rec.jml_halaman)} hal</p>
            )}
          </div>
          <button data-testid={`edit-btn-${rec.id}`} onClick={() => onEdit(rec)}
            className="p-1.5 hover:bg-emerald-50 rounded-xl transition-colors ml-1">
            <Pencil size={14} className="text-emerald-500" />
          </button>
        </div>
      </div>
      {rec.catatan && (
        <p className="text-[10px] text-gray-400 italic mt-1.5 border-t border-gray-50 pt-1.5">{rec.catatan}</p>
      )}
    </div>
  );
}

function TasmiCard({ rec, santriName, onEdit }) {
  const juzInfo = rec.tipe === 'multi'
    ? `Juz ${rec.juz_dari}–${rec.juz_sampai} (${rec.jumlah_juz} Juz)`
    : rec.juz ? `Juz ${rec.juz}` : '-';
  const nilai = rec.penilaian || rec.keterangan || '-';
  return (
    <div data-testid={`riwayat-card-${rec.id}`}
      className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-gray-100">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <span className="text-amber-700 font-bold text-xs">{santriName?.charAt(0) ?? '?'}</span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate">{santriName || '-'}</p>
            <p className="text-[10px] text-gray-400">{fmtDate(rec.tanggal)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="text-right">
            <p className="text-xs font-bold text-amber-700">{juzInfo}</p>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
              nilai === 'Lulus' || nilai === 'A' ? 'bg-emerald-100 text-emerald-700'
                : nilai === 'Remedial' ? 'bg-red-100 text-red-600'
                : 'bg-gray-100 text-gray-600'
            }`}>{nilai}</span>
          </div>
          <button data-testid={`edit-btn-${rec.id}`} onClick={() => onEdit(rec)}
            className="p-1.5 hover:bg-amber-50 rounded-xl transition-colors ml-1">
            <Pencil size={14} className="text-amber-500" />
          </button>
        </div>
      </div>
    </div>
  );
}

function UjianCard({ rec, santriName, onEdit }) {
  return (
    <div data-testid={`riwayat-card-ujian-${rec.id}`}
      className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-gray-100">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
            <span className="text-purple-700 font-bold text-xs">{santriName?.charAt(0) ?? '?'}</span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate">{santriName || '-'}</p>
            <p className="text-[10px] text-gray-400">{rec.semester}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="text-right">
            {rec.tajwid_nilai && (
              <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">T: {rec.tajwid_nilai}</span>
            )}
            <p className="text-[10px] text-gray-400 mt-0.5">{fmtDate(rec.created_at)}</p>
          </div>
          <button data-testid={`edit-btn-ujian-${rec.id}`} onClick={() => onEdit(rec)}
            className="p-1.5 hover:bg-purple-50 rounded-xl transition-colors ml-1">
            <Pencil size={14} className="text-purple-500" />
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {rec.mhq?.map((m, i) => (
          <span key={i} className="text-[10px] bg-blue-50 text-blue-700 font-semibold px-2 py-0.5 rounded-md">MHQ Juz {m.juz} · {m.nilai}</span>
        ))}
        {rec.murojaah?.map((m, i) => (
          <span key={i} className="text-[10px] bg-purple-50 text-purple-700 font-semibold px-2 py-0.5 rounded-md">Mur Juz {m.juz} · {m.nilai}</span>
        ))}
      </div>
    </div>
  );
}

/* ─────────── MAIN PAGE ─────────── */
export default function GuruRiwayat() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('ziyadah');
  const [setoranAll, setSetoranAll] = useState([]);
  const [tasmiAll, setTasmiAll] = useState([]);
  const [ujianAll, setUjianAll] = useState([]);
  const [santriList, setSantriList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSantri, setFilterSantri] = useState('');
  const [filterBulan, setFilterBulan] = useState('');
  const [editRec, setEditRec] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/setoran/riwayat`, { withCredentials: true }).then(r => r.data),
      axios.get(`${API}/tasmi`, { withCredentials: true }).then(r => r.data),
      axios.get(`${API}/ujian-tahfidz`, { withCredentials: true }).then(r => r.data),
      axios.get(`${API}/santri`, { withCredentials: true }).then(r => r.data),
    ]).then(([s, t, u, sl]) => {
      setSetoranAll(s); setTasmiAll(t); setUjianAll(u); setSantriList(sl);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const nameOf = (sid) => santriList.find(x => x.id === sid)?.name || '-';

  const applyFilters = (items, dateField) =>
    items.filter(item => {
      const name = nameOf(item.santri_id).toLowerCase();
      const matchName = !filterSantri || name.includes(filterSantri.toLowerCase());
      const date = (item[dateField] || '').slice(0, 7);
      const matchBulan = !filterBulan || date === filterBulan;
      return matchName && matchBulan;
    });

  const filtered = useMemo(() => {
    const setoranByJenis = (jenis) => {
      const raw = setoranAll.filter(s => {
        const j = (s.jenis || '').toLowerCase();
        if (jenis === 'ziyadah') return j === 'ziyadah';
        if (jenis === 'murojaah') return j === 'murojaah' || j === 'murajaah';
        if (jenis === 'binnadzar') return ['bin_nadzar', 'bin_nadhar', 'binnadzar', 'bin nadhar'].includes(j);
        return false;
      });
      return applyFilters(raw, 'tanggal');
    };
    return {
      ziyadah:  setoranByJenis('ziyadah'),
      murojaah: setoranByJenis('murojaah'),
      binnadzar:setoranByJenis('binnadzar'),
      tasmi:    applyFilters(tasmiAll, 'tanggal'),
      ujian:    applyFilters(ujianAll, 'created_at'),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setoranAll, tasmiAll, ujianAll, santriList, filterSantri, filterBulan]);

  const currentItems = filtered[activeTab] || [];

  // After edit save: update local state in-place
  const handleSaved = (updated) => {
    if (activeTab === 'tasmi') {
      setTasmiAll(prev => prev.map(r => r.id === updated.id ? updated : r));
    } else if (activeTab === 'ujian') {
      setUjianAll(prev => prev.map(r => r.id === updated.id ? updated : r));
    } else {
      setSetoranAll(prev => prev.map(r => r.id === updated.id ? updated : r));
    }
    setEditRec(null);
  };

  // After delete: remove from local state
  const handleDeleted = (deletedId) => {
    if (activeTab === 'tasmi') {
      setTasmiAll(prev => prev.filter(r => r.id !== deletedId));
    } else if (activeTab === 'ujian') {
      setUjianAll(prev => prev.filter(r => r.id !== deletedId));
    } else {
      setSetoranAll(prev => prev.filter(r => r.id !== deletedId));
    }
    setEditRec(null);
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto min-h-screen bg-gray-50 pb-24">

        {/* Header */}
        <div className="bg-white px-4 pt-4 pb-3 shadow-sm sticky top-0 z-20">
          <div className="flex items-center gap-3 mb-3">
            <button onClick={() => navigate(-1)} data-testid="riwayat-back-btn"
              className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div className="flex items-center gap-2">
              <History size={18} className="text-emerald-600" />
              <h1 className="text-base font-bold text-gray-800">Riwayat Setoran</h1>
            </div>
          </div>
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input data-testid="riwayat-filter-santri" type="text" placeholder="Cari nama santri..."
                value={filterSantri} onChange={e => setFilterSantri(e.target.value)}
                className="w-full pl-8 pr-7 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 bg-gray-50" />
              {filterSantri && (
                <button onClick={() => setFilterSantri('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-200 rounded-full">
                  <X size={12} className="text-gray-400" />
                </button>
              )}
            </div>
            <input data-testid="riwayat-filter-bulan" type="month" value={filterBulan}
              onChange={e => setFilterBulan(e.target.value)}
              className="px-2 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 bg-gray-50 w-36" />
          </div>
          <div className="flex gap-1 overflow-x-auto pb-0.5 scrollbar-hide">
            {TABS.map(tab => {
              const count = filtered[tab.key]?.length ?? 0;
              const isActive = activeTab === tab.key;
              return (
                <button key={tab.key} data-testid={`riwayat-tab-${tab.key}`} onClick={() => setActiveTab(tab.key)}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                    isActive ? 'bg-emerald-600 text-white shadow-sm' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}>
                  {tab.label}
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                    isActive ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* List */}
        <div className="px-4 py-3 space-y-2">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-400">Memuat riwayat...</p>
            </div>
          ) : currentItems.length === 0 ? (
            <div data-testid="riwayat-empty"
              className="flex flex-col items-center justify-center py-16 gap-3 bg-white rounded-2xl shadow-sm border border-gray-100">
              <History size={32} className="text-gray-300" />
              <p className="text-sm text-gray-400">Tidak ada riwayat ditemukan</p>
              {(filterSantri || filterBulan) && (
                <button onClick={() => { setFilterSantri(''); setFilterBulan(''); }}
                  className="text-xs text-emerald-600 font-semibold">Hapus filter</button>
              )}
            </div>
          ) : (
            <>
              <p className="text-[10px] text-gray-400 px-1">{currentItems.length} data</p>
              {currentItems.map(item =>
                activeTab === 'tasmi' ? (
                  <TasmiCard key={item.id} rec={item} santriName={nameOf(item.santri_id)} onEdit={setEditRec} />
                ) : activeTab === 'ujian' ? (
                  <UjianCard key={item.id} rec={item} santriName={nameOf(item.santri_id)} onEdit={setEditRec} />
                ) : (
                  <SetoranCard key={item.id} rec={item} santriName={nameOf(item.santri_id)} onEdit={setEditRec} />
                )
              )}
            </>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editRec && (
        <EditModal
          rec={editRec}
          tabKey={activeTab}
          onClose={() => setEditRec(null)}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
        />
      )}
    </Layout>
  );
}
